import { useQuery } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

/**
 * Build where clause for business filtering
 * @param {string} enabledFilter - Filter by enabled status ('all', 'enabled', 'disabled')
 * @param {string} searchTerm - Search term for business name
 * @returns {Object} Where clause object
 */
export const buildBusinessWhereClause = (enabledFilter, searchTerm) => {
  const conditions = [];

  // Add enabled filter condition
  if (enabledFilter === "enabled" || enabledFilter === "disabled") {
    conditions.push({
      attribute: "enabled",
      value: enabledFilter === "enabled",
    });
  }

  // Add search condition for business name
  if (searchTerm && searchTerm.trim() !== "") {
    const _conditions = [
      {
        attribute: "name",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
      {
        attribute: "email",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
      {
        attribute: "phone",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
    ];
    conditions.push({
      conector: "OR",
      conditions: _conditions,
    });
  }

  return {
    conditions,
    conector: "AND",
  };
};

/**
 * Default parameters to send with business requests
 */
export const businessParamsToSend = {
  params: "disabled_reason,city(name),distance,enabled,logo,name,franchise_id",
};

/**
 * Hook to get businesses with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.enabledFilter - Filter by enabled status ('all', 'enabled', 'disabled')
 * @param {string} params.searchTerm - Search term for business name
 * @param {number} params.page - Page number (default: 1); ignored when includePagination is false
 * @param {number} params.pageSize - Page size (default: 10); ignored when includePagination is false
 * @param {boolean} [params.includePagination=true] - When false, `page` and `page_size` are not sent to the API (full list in one response, if supported).
 * @param {boolean} [params.includeWhere=true] - When false, no `where` clause is sent (API returns unfiltered list when supported).
 * @param {string|null} [params.fields=null] - When set, sent as `params` (CSV field list) instead of {@link businessParamsToSend}.
 * @returns {Object} Query result with businesses and pagination
 */
export const useBusinesses = (params = {}) => {
  const {
    enabledFilter = "all",
    searchTerm = "",
    page = 1,
    pageSize = 10,
    includePagination = true,
    includeWhere = true,
    fields = null,
  } = params;

  const whereClause = buildBusinessWhereClause(enabledFilter, searchTerm);

  const fieldParams =
    fields != null && String(fields).trim() !== ""
      ? { params: String(fields).trim() }
      : businessParamsToSend;

  const queryParams = {
    ...fieldParams,
    ...(includeWhere ? { where: whereClause } : {}),
    ...(includePagination ? { page, page_size: pageSize } : {}),
  };

  const queryString = buildQueryParams(queryParams);

  const fieldsKey = fields != null ? String(fields).trim() : "default";

  return useQuery({
    queryKey: includePagination
      ? [
          "businesses",
          includeWhere ? enabledFilter : "_",
          includeWhere ? searchTerm : "_",
          page,
          pageSize,
          fieldsKey,
        ]
      : [
          "businesses",
          includeWhere ? enabledFilter : "_",
          includeWhere ? searchTerm : "_",
          "all",
          fieldsKey,
        ],
    queryFn: async () => {
      const response = await apiClient.get(
        `/backoffice/businesses${queryString ? `?${queryString}` : ""}`,
      );
      return {
        businesses: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("business", "list"),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Refetch stale data on mount (e.g. after create while list was unmounted)
    refetchOnReconnect: false,
    retry: false,
  });
};

export default {
  useBusinesses,
};
